import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaBook,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaChartBar,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const levels = ["A1", "A2", "B1"];
type AttendanceRow = {
  classNumber: number;
  date: string;
  status: "PRESENT" | "ABSENT";
};

const StudentAttendanceOverview: React.FC = () => {
  const [selectedLevel, setSelectedLevel] = useState("A1");
  const [rows, setRows] = useState<AttendanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { student } = useAuth();

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/student/my-attendance?level=${selectedLevel}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load");
        return r.json();
      })
      .then((data: AttendanceRow[]) => setRows(data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedLevel]);

  const present = rows.filter((r) => r.status === "PRESENT").length;
  const total = rows.length;
  const attendancePercentage =
    total > 0 ? Math.round((present / total) * 100) : 0;

  const getPercentageColor = (percentage: number) => {
    if (percentage >= 80) return "text-emerald-600";
    if (percentage >= 60) return "text-blue-600";
    if (percentage >= 40) return "text-amber-600";
    return "text-rose-600";
  };

  const getPercentageBg = (percentage: number) => {
    if (percentage >= 80) return "bg-emerald-50 border-emerald-200";
    if (percentage >= 60) return "bg-blue-50 border-blue-200";
    if (percentage >= 40) return "bg-amber-50 border-amber-200";
    return "bg-rose-50 border-rose-200";
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaCalendarAlt />}
        title="My Attendance Overview"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "My Attendance" },
        ]}
      />

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Welcome Section */}
        <div className="mb-10 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-xl p-6 shadow-lg">
          <h2 className="text-3xl font-bold mb-2 text-white flex items-center gap-2">
            <FaCalendarAlt />
            {student?.firstName
              ? `Welcome, ${student.firstName} 👋`
              : "Welcome 👋"}
          </h2>
          <p className="text-white opacity-90 text-base">
            Select a level to see your attendance records and track your
            progress!
          </p>
        </div>

        {/* Level Selection and Summary Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            {/* Level Selection */}
            <div className="flex items-center gap-4">
              <div className="bg-sky-100 p-3 rounded-lg">
                <FaBook className="text-sky-600 text-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Level:
                </label>
                <select
                  className="border border-gray-300 px-4 py-2 rounded-lg text-lg font-semibold text-[#009fe3] focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all shadow-sm"
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                >
                  {levels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Attendance Summary */}
            <div
              className={`rounded-xl p-4 border ${getPercentageBg(
                attendancePercentage
              )} flex-shrink-0`}
            >
              <div className="flex items-center gap-3">
                <div className="bg-white p-2 rounded-lg shadow-sm">
                  <FaChartBar
                    className={`text-xl ${getPercentageColor(
                      attendancePercentage
                    )}`}
                  />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Attendance Rate
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-2xl font-bold ${getPercentageColor(
                        attendancePercentage
                      )}`}
                    >
                      {present}/{total}
                    </span>
                    {total > 0 && (
                      <span
                        className={`text-lg font-bold ${getPercentageColor(
                          attendancePercentage
                        )}`}
                      >
                        ({attendancePercentage}%)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200 text-rose-800 rounded-xl p-4 mb-8 shadow-sm">
            <div className="flex items-center gap-2">
              <FaTimesCircle className="text-rose-600" />
              <span className="font-semibold">Error:</span> {error}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your attendance records...</p>
          </div>
        ) : rows.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <div className="bg-blue-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <FaCalendarAlt className="text-3xl text-[#009fe3]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              No attendance records found
            </h3>
            <p className="text-gray-600 mb-6">
              You have no attendance records for {selectedLevel} level yet.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-sm text-blue-800">
                Your attendance will appear here once you start attending
                classes for this level.
              </p>
            </div>
          </div>
        ) : (
          /* Attendance Table */
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Table Header */}
            <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <FaCalendarAlt />
                {selectedLevel} Level Attendance Records
              </h3>
              <p className="text-cyan-100 text-sm mt-1">
                {rows.length} total classes recorded
              </p>
            </div>

            {/* Progress Bar */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Overall Attendance Progress
                </span>
                <span
                  className={`text-sm font-bold ${getPercentageColor(
                    attendancePercentage
                  )}`}
                >
                  {attendancePercentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${
                    attendancePercentage >= 80
                      ? "bg-emerald-500"
                      : attendancePercentage >= 60
                      ? "bg-blue-500"
                      : attendancePercentage >= 40
                      ? "bg-amber-500"
                      : "bg-rose-500"
                  }`}
                  style={{ width: `${attendancePercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Table Content */}
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Class #
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Date
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={row.classNumber}
                      className={`border-b border-gray-100 ${
                        index % 2 === 1 ? "bg-gray-50" : "bg-white"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center">
                            <span className="text-sm font-semibold text-gray-700">
                              {row.classNumber}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">
                            Class {row.classNumber}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-gray-700">
                          <FaCalendarAlt className="text-xs text-gray-400" />
                          {row.date}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.status === "PRESENT" ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <FaCheckCircle className="text-emerald-500" />
                            Present
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                            <FaTimesCircle className="text-rose-500" />
                            Absent
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Table Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Showing {rows.length} attendance records for {selectedLevel}{" "}
                  level
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                    <span className="text-gray-600">{present} Present</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-rose-500 rounded-full"></div>
                    <span className="text-gray-600">
                      {total - present} Absent
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentAttendanceOverview;
